// Login screen: sign in or register on the configured grid. The grid picker
// is a list of grid names (never addresses) and stays out of sight unless
// "show grid selection" is on in settings, as the desktop viewer does it.
// Grids come from the build (VITE_GRIDS), plus any the user added by address
// through the options popover; their names come from the grids themselves.

import { createAccount, fetchGridInfo, login, normalizeGridUrl, type GridInfo, type Keeping, type NewAccount } from "@/net/grid";
import { isEditable, isTouch } from "@/input/device";
import type { LoginResponse } from "@/net/messages";
import { fetchLastNames, tidyLogin, type LastNames } from "@/net/names";
import { plainReason } from "@/net/closing";
import { el, clear, loadSetting, saveSetting } from "./dom";
import { chatText } from "./chatview";
import { uiLayer } from "./scale";
import { Backdrop } from "./backdrop";
import { wordmark } from "./logo";
import { SignUpForm } from "./signup";
import "./arrival.css";

export interface LoginResult {
  info: GridInfo;
  response: LoginResponse;
  gridUrl: string;
}

export interface KnownGrid {
  name: string;
  url: string;
}

const LOCAL_GRID: KnownGrid = {
  name: "Local grid",
  url: `${location.protocol === "https:" ? "https" : "http"}://${location.hostname || "127.0.0.1"}:7001`,
};

/** Grids this build ships with; the local grid is only offered in development. */
function builtInGrids(): KnownGrid[] {
  const grids: KnownGrid[] = [];
  try {
    const raw = import.meta.env.VITE_GRIDS as string | undefined;
    if (raw) grids.push(...(JSON.parse(raw) as KnownGrid[]).map((g) => ({ name: g.name, url: normalizeGridUrl(g.url) })));
  } catch {
    // A malformed list leaves only the local grid.
  }
  if (import.meta.env.DEV) grids.push(LOCAL_GRID);
  return grids;
}

/** The grids to choose from: those this build ships with, and no others. */
function knownGrids(): KnownGrid[] {
  const all = builtInGrids();
  return all.length > 0 ? all : [LOCAL_GRID];
}

/** The grid this build talks to unless the user picked another. */
export const DEFAULT_GRID: string = (import.meta.env.VITE_GRID_URL as string | undefined) || builtInGrids()[0]?.url || LOCAL_GRID.url;

/** Development builds prefill the local grid's seeded owner account. */
const DEV_LOGIN = import.meta.env.DEV ? { username: "admin", password: "Starfall-Admin-2026!" } : null;

/** One label-and-value line in the classic grid-status box. */
function line2(label: string, value: string, tone = ""): HTMLElement {
  return el("div", { class: "classic-row" },
    el("span", { class: "k" }, label),
    el("span", { class: `v ${tone}` }, value),
  );
}

/** The shortest password the grid takes, as the account window has it. */
const SHORTEST_PASSWORD = 8;

/**
 * This device kept logged in to a grid: the name it logs in as and the
 * token the grid gave it to come back with instead of the password (each
 * works once; the grid answers with the next). The password is never kept.
 */
interface KeptLogin {
  grid: string;
  name: string;
  /** Who it is, as the grid last called them, for the card shown in the password's place. */
  display?: string;
  token: string;
}

/** The login this device is kept in to the grid at this address, if any. */
function keptLogin(grid: string): KeptLogin | null {
  const kept = loadSetting<KeptLogin | null>("keptLogin", null);
  return kept && kept.grid === grid && kept.token ? kept : null;
}

/** Where the terms of service are, next to the viewer itself. */
export const TERMS_URL = `${import.meta.env.BASE_URL}terms.html`;

/** Where the privacy policy is: on the website, which keeps the one copy (web/src/pages/privacy.ts in starfall-server). */
export const PRIVACY_URL = "https://primverse.world/privacy";

/**
 * The world behind the login: a slow loop filmed at Seaward's welcome and
 * at Primverse HQ next door in Riverbend (starfall-server tools/seaward),
 * and its first frame to show until the loop is playing, or instead of it
 * for anybody who has asked for less motion or less data.
 */
const MEDIA = `${import.meta.env.BASE_URL}media/login/`;
const LOOP_SOURCES: Array<[string, string]> = [
  [`${MEDIA}seaward-loop.webm`, 'video/webm; codecs="vp9"'],
  [`${MEDIA}seaward-loop.mp4`, 'video/mp4; codecs="avc1.640028"'],
];
const POSTER = `${MEDIA}seaward-poster.jpg`;
/** What the picture is, said quietly along the foot. */
const CREDIT = "Seaward and Riverbend, filmed in Primverse";
/** Said under the name until the grid says its own words. */
const TAGLINE = "It all starts with an idea.";

/**
 * Whether this person would rather not have the picture moving, or would
 * rather not pay for it: the loop is some 5 MB, fetched at every login,
 * which a phone on a mobile network or a slow one feels.
 */
function stillOnly(): boolean {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; type?: string; effectiveType?: string } }).connection;
  if (connection?.saveData === true) return true;
  if (isTouch() && (connection?.type === "cellular" || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType ?? ""))) return true;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/** Whether this browser can draw the world: with WebGPU, with WebGL 2 alone, or not at all. */
export type Drawing = "webgpu" | "webgl2" | "none";

/**
 * Ask the browser, before anybody logs in, whether it can draw the world.
 * Without WebGPU, three falls back to WebGL 2, which works but runs a good
 * deal more slowly: older iPhones and iPads, Firefox on Android, Android 11
 * and before, and graphics a browser has turned WebGPU off for. With
 * neither, there is no getting in at all, and better to say so now than
 * after the grid has let somebody in.
 */
export async function drawingSupport(): Promise<Drawing> {
  try {
    if (await navigator.gpu?.requestAdapter()) return "webgpu";
  } catch {
    // No adapter: see whether WebGL 2 is there instead.
  }
  try {
    const gl = document.createElement("canvas").getContext("webgl2");
    if (gl) {
      // Asked only to know, so given straight back rather than left for
      // the collector: browsers allow only so many at once.
      gl.getExtension("WEBGL_lose_context")?.loseContext();
      return "webgl2";
    }
  } catch {
    // Neither.
  }
  return "none";
}

/** What to say when there is no drawing the world here, in words for the kind of device it is. */
function cannotDrawMessage(): string {
  return isTouch()
    ? "This browser can't show Primverse. On iPhone or iPad, update to iOS 26; on Android, use Chrome on Android 12 or later."
    : "This browser can't show Primverse. Try the latest Chrome, Edge, Firefox or Safari, with hardware acceleration turned on in its settings.";
}

/**
 * Whether to suggest putting Primverse on the home screen: an iPhone's
 * Safari has no full screen for a page, and only a page opened from the
 * home screen loses Safari's bars. Once, and never once it is there.
 */
function suggestHomeScreen(): boolean {
  if (!/iPhone|iPod/.test(navigator.userAgent) || loadSetting("hint.homeScreen", false)) return false;
  const installed = (navigator as Navigator & { standalone?: boolean }).standalone === true || window.matchMedia?.("(display-mode: standalone), (display-mode: fullscreen)").matches === true;
  return !installed;
}

/** The filmed scene: its first frame at once, the loop over it once it plays. */
function filmedScene(): { root: HTMLElement; video: HTMLVideoElement | null } {
  const poster = el("img", { src: POSTER, alt: "", decoding: "async" });
  const root = el("div", { class: "scene", "aria-hidden": "true" }, poster);
  if (stillOnly()) return { root, video: null };
  const video = el("video", { playsinline: true, loop: true, preload: "auto", disablepictureinpicture: true });
  // Set as properties too: autoplay is only allowed muted, and the
  // attribute alone does not mute everywhere.
  video.muted = true;
  video.autoplay = true;
  for (const [src, type] of LOOP_SOURCES) video.append(el("source", { src, type }));
  video.addEventListener("playing", () => video.classList.add("playing"), { once: true });
  root.append(video);
  void video.play().catch(() => {
    // Not allowed to play by itself: the still stays, which is fine.
  });
  return { root, video };
}

/**
 * Show the terms of service and ask for agreement. Resolves true when the
 * person agrees, false when they decline.
 */
function askToAgree(version: string): Promise<boolean> {
  return new Promise((resolve) => {
    const frame = el("iframe", { class: "terms-frame", src: TERMS_URL, title: "Terms of Service" });
    const finish = (agreed: boolean) => {
      overlay.remove();
      resolve(agreed);
    };
    const overlay = el("div", { class: "terms-overlay" },
      el("div", { class: "terms-dialog glass", role: "dialog", "aria-modal": "true" },
        el("h2", {}, "Terms of Service"),
        el("p", { class: "muted" },
          `Please read these terms (version ${version}) and agree to them to come in. The `,
          el("a", { href: PRIVACY_URL, target: "_blank", rel: "noopener" }, "Privacy Policy"),
          " says what we keep about you. You can read both again at any time from the Help menu.",
        ),
        frame,
        el("div", { class: "terms-actions" },
          el("button", { type: "button", class: "ghost", onClick: () => finish(false) }, "Decline"),
          el("button", { type: "button", class: "primary", onClick: () => finish(true) }, "I agree"),
        ),
      ),
    );
    // In the interface layer, so it is drawn at the interface's size.
    uiLayer().append(overlay);
  });
}

export class LoginScreen {
  readonly root: HTMLElement;
  private status: HTMLElement;
  private motd: HTMLElement;
  private gridLine: HTMLElement;
  private gridField: HTMLElement;
  private info: GridInfo | null = null;
  /** Whether the grid has been asked yet, answered or not. */
  private probed = false;
  private busy = false;
  private registering = false;
  /** The grid's last names to choose from when making an account; null for a grid that has none, which asks a username. */
  private pool: LastNames | null = null;
  private signUp: SignUpForm;
  /** The grid's website, where accounts are made (its brand's website); null for none. */
  private website: string | null = null;
  private termsCheck: HTMLInputElement;
  private termsField: HTMLElement;
  private gridSelect: HTMLSelectElement;
  private gridUrl: string;
  private userInput: HTMLInputElement;
  private userLabel: HTMLElement;
  private userField: HTMLElement;
  private passInput: HTMLInputElement;
  private passField: HTMLElement;
  /** Asked with the password: whether to keep this device logged in. */
  private keepCheck: HTMLInputElement;
  private keepField: HTMLElement;
  /** In the name's and the password's place while this device is kept logged in. */
  private keptField: HTMLElement;
  private keptInitial: HTMLElement;
  private keptName: HTMLElement;
  private displayInput: HTMLInputElement;
  /** "last", "home", or "region" for the region named in regionInput. */
  private startAt = loadSetting("startAt", "last");
  private startButtons: HTMLButtonElement[] = [];
  private regionInput: HTMLInputElement;
  private startField: HTMLElement;
  private submit: HTMLButtonElement;
  private toggle: HTMLButtonElement;
  private displayField: HTMLElement;
  private optionsOpen = false;
  private options: HTMLElement;
  private sky = new Backdrop();
  /** A line under the status for something said only once, such as compatibility mode. */
  private note: HTMLElement;
  /** The home screen suggestion, while there is one to show. */
  private homeTip: HTMLElement | null = null;
  /** Whether the world can be drawn here, once known; see drawingSupport. */
  private drawing: Drawing | null = null;
  private statusBox!: HTMLElement;
  private newsBox!: HTMLElement;
  // The classic layout is kept but not offered for now: always the cinematic.
  private classic = false;
  // The cinematic layout's parts, and the classic's, built once and put on
  // the screen by applyLayout.
  private scene: { root: HTMLElement; video: HTMLVideoElement | null } | null = null;
  private brand!: HTMLElement;
  private hero!: HTMLElement;
  private card!: HTMLElement;
  private cardTitle!: HTMLElement;
  private foot!: HTMLElement;
  private bar!: HTMLElement;
  private vignette!: HTMLElement;
  private stack!: HTMLElement;
  private tagline!: HTMLElement;

  constructor(private onLogin: (result: LoginResult) => void) {
    const showGrid = loadSetting("showGridSelector", false);
    this.gridUrl = showGrid ? loadSetting("grid", DEFAULT_GRID) : DEFAULT_GRID;
    this.gridSelect = el("select");
    this.fillGrids();
    // A name is typed as 'First Last' or 'first.last', an older account's
    // username as before, or the account's email address. The grid works
    // out which. Hints
    // for the on-screen keyboard: the return key moves on to the password,
    // where it logs in.
    this.userInput = el("input", { type: "text", placeholder: "First Last, username or email", autocomplete: "username", autocapitalize: "words", autocorrect: "off", spellcheck: "false", enterkeyhint: "next" });
    this.userInput.value = loadSetting("username", DEV_LOGIN?.username ?? "");
    this.passInput = el("input", { type: "password", placeholder: "Password", autocomplete: "current-password", enterkeyhint: "go" });
    if (DEV_LOGIN && this.userInput.value === DEV_LOGIN.username) this.passInput.value = DEV_LOGIN.password;
    this.passField = el("div", { class: "field" }, el("label", {}, "Password"), this.passInput);
    this.keepCheck = el("input", { type: "checkbox", role: "switch" });
    this.keepCheck.checked = loadSetting("keepLoggedIn", false);
    this.keepField = el("label", { class: "keep-in" }, el("span", {}, "Keep me logged in on this device"), this.keepCheck);
    // Kept logged in, whose account it is stands in a card of its own:
    // nothing to type, only the button to press.
    this.keptInitial = el("span", { class: "initial", "aria-hidden": "true" });
    this.keptName = el("b", {});
    this.keptField = el("div", { class: "kept" },
      this.keptInitial,
      el("span", { class: "who" }, this.keptName, el("span", {}, "Kept logged in on this device")),
      el("button", { class: "link", type: "button", onClick: () => this.forgetKept() }, "Not you?"),
    );
    this.displayInput = el("input", { type: "text", placeholder: "Display name (optional)", autocomplete: "nickname", autocapitalize: "words" });
    this.status = el("div", { class: "status" });
    this.note = el("div", { class: "note", role: "status" });
    this.note.hidden = true;
    this.motd = el("div", { class: "motd" });
    this.gridLine = el("div", { class: "grid-line" }, "Contacting grid…");
    this.submit = el("button", { class: "primary", type: "submit" }, "Log in");
    // An account is made on the grid's website, where the address it is
    // made with is confirmed; a grid without one makes it here.
    this.toggle = el("button", {
      class: "link",
      type: "button",
      onClick: () => {
        if (this.registering) this.setRegistering(false);
        else if (this.website) window.open(`${this.website}/join`, "_blank", "noopener");
        else void this.openSignUp();
      },
    }, "Create an account");
    this.userLabel = el("label", {}, "Name");
    this.userField = el("div", { class: "field" }, this.userLabel, this.userInput);
    this.signUp = new SignUpForm(() => this.gridUrl);
    this.signUp.root.style.display = "none";
    this.displayField = el("div", { class: "field" }, el("label", {}, "Display name"), this.displayInput);
    this.displayField.style.display = "none";
    this.termsCheck = el("input", { type: "checkbox" });
    this.termsField = el("label", { class: "terms-agree" },
      this.termsCheck,
      " I have read and agree to the ",
      el("a", { href: TERMS_URL, target: "_blank", rel: "noopener" }, "Terms of Service"),
      ". The ",
      el("a", { href: PRIVACY_URL, target: "_blank", rel: "noopener" }, "Privacy Policy"),
      " says what we keep about you.",
    );
    this.termsField.style.display = "none";
    this.gridField = el("div", { class: "field grid-field" }, el("label", {}, "Grid"), this.gridSelect);
    this.gridField.style.display = showGrid ? "" : "none";

    // Where to come in: the last place, home, or a region by its name, as
    // Second Life's login has it. The name is looked for as you log in; a
    // region there is none of takes you home, or to the welcome area.
    const start = el("div", { class: "segmented" });
    for (const [value, label] of [["last", "Last location"], ["home", "Home"], ["region", "Region"]]) {
      const b = el("button", { type: "button", class: value === this.startAt ? "on" : "", onClick: () => this.setStart(value) }, label);
      b.dataset.start = value;
      this.startButtons.push(b);
      start.append(b);
    }
    this.regionInput = el("input", { type: "text", placeholder: "Region name", autocomplete: "off", spellcheck: "false", autocapitalize: "words", enterkeyhint: "go" });
    this.regionInput.value = loadSetting("startRegion", "");
    this.regionInput.style.display = this.startAt === "region" ? "" : "none";
    this.startField = el("div", { class: "field" }, el("label", {}, "Start at"), start, this.regionInput);

    // Options popover: the few things worth changing before you are in.
    const gridToggle = el("input", { type: "checkbox" });
    gridToggle.checked = showGrid;
    gridToggle.addEventListener("change", () => {
      saveSetting("showGridSelector", gridToggle.checked);
      this.gridField.style.display = gridToggle.checked ? "" : "none";
      if (!gridToggle.checked) this.chooseGrid(DEFAULT_GRID);
    });
    const themeToggle = el("input", { type: "checkbox" });
    themeToggle.checked = document.documentElement.dataset.theme === "light";
    themeToggle.addEventListener("change", () => {
      const next = themeToggle.checked ? "light" : "dark";
      document.documentElement.dataset.theme = next;
      saveSetting("theme", next);
    });
    this.options = el("div", { class: "options glass" },
      el("label", {}, gridToggle, " Show grid selection"),
      el("label", {}, themeToggle, " Light theme"),
    );
    const optionsButton = el("button", { class: "ghost icon options-button", type: "button", title: "Options", "aria-label": "Options", onClick: (e: Event) => { e.stopPropagation(); this.setOptions(!this.optionsOpen); } }, "⚙");
    // Any press elsewhere puts the options away, a finger's as much as a
    // mouse's. Taken off with the screen: it used to stay on the window
    // for the whole of the session after.
    window.addEventListener("pointerdown", this.pressedElsewhere, { capture: true });

    const form = el(
      "form",
      { onSubmit: (e: Event) => this.handleSubmit(e) },
      this.gridField,
      this.keptField,
      this.userField,
      this.signUp.root,
      this.passField,
      this.keepField,
      this.displayField,
      this.termsField,
      this.startField,
      this.submit,
      this.status,
      this.note,
      el("div", { class: "actions" },
        this.toggle,
        // There is no mail to send a reset to, so a new password comes from
        // the grid's staff, who can set one from the Manager.
        el("button", { class: "link", type: "button", onClick: () => this.setStatus("Forgotten it? The grid's staff can set you a new password: ask one of them, and they will check it is you first.") }, "Forgotten your password?"),
      ),
    );

    // Classic: the name over the card, the card holding nothing but the
    // form. Cinematic: the name large on the left over the picture, the
    // form in glass on the right, the grid's state along the foot.
    this.tagline = el("p", { class: "tagline" }, TAGLINE);
    this.brand = el("div", { class: "brand" }, wordmark(56), el("p", { class: "tagline" }, "Your world, in a browser."));
    this.hero = el("div", { class: "hero" }, wordmark(64), this.tagline);
    this.cardTitle = el("h1", { class: "card-title" }, "Sign in");
    this.card = el("div", { class: "login-card glass" }, optionsButton, this.options, this.cardTitle, form);
    this.foot = el("div", { class: "login-foot" });
    this.bar = el("div", { class: "login-bar" });
    this.vignette = el("div", { class: "vignette" });
    this.stack = el("div", { class: "login-stack" });
    // Shown only by the classic layout, which boxes the grid's state off in
    // the corner and gives its message of the day a panel of its own.
    this.statusBox = el("div", { class: "classic-status" });
    this.newsBox = el("div", { class: "classic-news" });
    // Under the card's heading, in the flow of the card, where it covers
    // nothing however short the screen.
    if (suggestHomeScreen()) {
      this.homeTip = this.homeScreenTip();
      this.cardTitle.after(this.homeTip);
    }
    this.root = el("div", { class: "login" });
    this.applyLayout();

    this.gridSelect.addEventListener("change", () => this.chooseGrid(this.gridSelect.value));
    this.showKept();
    void this.probe();
    void this.checkDrawing();
    // Ready to type into at a desk. On a touch screen that would put the
    // keyboard over half the screen before anybody had asked for it.
    const first = this.kept() ? this.submit : this.userInput.value ? this.passInput : this.userInput;
    if (!(isTouch() && isEditable(first))) first.focus();
  }

  private readonly pressedElsewhere = (e: Event): void => {
    if (!(e.target as HTMLElement | null)?.closest?.(".options, .options-button")) this.setOptions(false);
  };

  /** Find out whether the world can be drawn here while the form is filled in, and say so if it cannot. */
  private async checkDrawing(): Promise<void> {
    this.drawing = await drawingSupport();
    if (this.drawing === "none") {
      this.submit.disabled = true;
      this.setStatus(cannotDrawMessage(), true);
      return;
    }
    // On a phone or a tablet, said once: WebGL 2 alone draws the world,
    // but slowly enough that the graphics start on Low.
    if (this.drawing === "webgl2" && isTouch() && !loadSetting("hint.compatibility", false)) {
      saveSetting("hint.compatibility", true);
      this.note.textContent = "This browser draws Primverse in compatibility mode, which is slower, so the graphics start on Low.";
      this.note.hidden = false;
    }
  }

  /** The suggestion to add Primverse to the home screen, with a way to put it away. */
  private homeScreenTip(): HTMLElement {
    const tip = el("div", { class: "login-tip", role: "note" }, el("span", {}, "For full screen, tap Share, then Add to Home Screen."));
    const done = () => {
      saveSetting("hint.homeScreen", true);
      tip.remove();
      this.homeTip = null;
    };
    tip.append(el("button", { type: "button", class: "ghost icon", "aria-label": "Dismiss", onClick: done }, "✕"));
    return tip;
  }

  /**
   * Put the chosen layout on the screen. The modern one is a column on a
   * starfield; the classic is a bar along the foot of a daylight sky with
   * the grid's state and its news boxed off around it.
   */
  private applyLayout(): void {
    this.root.classList.toggle("classic", this.classic);
    this.root.classList.toggle("cinematic", !this.classic);
    if (this.classic) {
      this.scene?.video?.pause();
      this.stack.replaceChildren(this.brand, this.card, this.foot);
      this.foot.replaceChildren(this.gridLine, this.motd);
      this.root.replaceChildren(this.sky.root, this.vignette, this.statusBox, this.stack, this.newsBox);
      this.sky.start();
    } else {
      this.sky.stop();
      this.scene ??= filmedScene();
      void this.scene.video?.play().catch(() => undefined);
      this.bar.replaceChildren(this.gridLine, this.motd, el("span", { class: "credit" }, CREDIT));
      this.root.replaceChildren(this.scene.root, el("div", { class: "shade" }), this.hero, this.card, this.bar);
    }
    this.renderClassicBoxes();
  }

  /** What the two corner panels say, when the classic layout shows them. */
  private renderClassicBoxes(): void {
    if (!this.classic) return;
    const info = this.info;
    clear(this.statusBox);
    this.statusBox.append(
      el("div", { class: "classic-title" }, "Grid status"),
      el("div", { class: "classic-rows" },
        line2("Status", info ? "Online" : this.probed ? "Unreachable" : "Contacting\u2026", info ? "up" : ""),
        line2("Grid", info?.grid_name ?? "\u2014"),
      ),
    );
    clear(this.newsBox);
    const motd = info?.motd?.trim();
    this.newsBox.append(
      el("div", { class: "classic-title" }, "News and announcements"),
      el("div", { class: "classic-news-body" }, motd ? chatText(motd, null) : "Nothing to report."),
    );
  }

  /** Called when the screen is taken off the page. */
  dispose(): void {
    this.sky.dispose();
    window.removeEventListener("pointerdown", this.pressedElsewhere, { capture: true });
    // Seen all the way to logging in: that counts as once.
    if (this.homeTip) saveSetting("hint.homeScreen", true);
    const video = this.scene?.video;
    if (video) {
      video.pause();
      // Let go of the loop rather than keep it decoded in the background.
      video.replaceChildren();
      video.load();
    }
  }

  private fillGrids(): void {
    this.gridSelect.replaceChildren();
    const grids = knownGrids();
    if (!grids.some((g) => g.url === this.gridUrl)) this.gridUrl = grids[0].url;
    for (const g of grids) {
      const opt = el("option", { value: g.url }, g.name);
      if (g.url === this.gridUrl) opt.selected = true;
      this.gridSelect.append(opt);
    }
  }

  private chooseGrid(url: string): void {
    this.gridUrl = url;
    this.fillGrids();
    saveSetting("grid", url);
    this.showKept();
    void this.probe();
  }

  /** The login this device is kept in to the grid shown, if any, while logging in rather than signing up. */
  private kept(): KeptLogin | null {
    return this.registering ? null : keptLogin(this.gridUrl);
  }

  /** The name and password asked for, or in their place the card of the account this device is kept logged in to. */
  private showKept(): void {
    const kept = this.kept();
    if (kept) {
      const who = kept.display?.trim() || kept.name;
      this.keptName.textContent = who;
      this.keptInitial.textContent = who.charAt(0).toUpperCase();
    }
    this.keptField.style.display = kept ? "" : "none";
    this.userField.style.display = kept || (this.registering && this.pool !== null) ? "none" : "";
    this.passField.style.display = kept ? "none" : "";
    // Offered only where the grid has said it will keep a device logged in.
    this.keepField.style.display = kept || this.registering || this.info?.keeps_logins !== true ? "none" : "";
  }

  /** Stop keeping this device logged in, and ask for a name and password again. */
  private forgetKept(): void {
    saveSetting("keptLogin", null);
    this.userInput.value = "";
    this.passInput.value = "";
    this.showKept();
    this.setStatus("");
    this.userInput.focus();
  }

  /**
   * Keep the token the grid answered with, or forget the one sent when no
   * other came back: it has been spent, or no longer works.
   */
  private keepToken(name: string, keeping: Keeping, response: LoginResponse): void {
    if (response.login_token) {
      const display = response.display_name || this.kept()?.display || name;
      saveSetting("keptLogin", { grid: this.gridUrl, name, display, token: response.login_token } satisfies KeptLogin);
    } else if (keeping.token) {
      saveSetting("keptLogin", null);
    }
  }

  private setOptions(open: boolean): void {
    this.optionsOpen = open;
    this.options.classList.toggle("open", open);
  }

  private setStart(value: string): void {
    this.startAt = value;
    saveSetting("startAt", value);
    for (const b of this.startButtons) b.classList.toggle("on", b.dataset.start === value);
    this.regionInput.style.display = value === "region" ? "" : "none";
    if (value === "region") this.regionInput.focus();
  }

  /** What the grid is asked for: the last place, home, or the region named. */
  private startLocation(): string {
    if (this.startAt !== "region") return this.startAt;
    const name = this.regionInput.value.trim();
    saveSetting("startRegion", name);
    return name || "last";
  }

  /**
   * The form for a new account: a first name and a last name from the
   * grid's list, or, on a grid without one, a username as before.
   */
  private async openSignUp(): Promise<void> {
    // Whether it asks an invite code is in what it says of itself.
    if (!this.info) await this.probe();
    if (!this.info) {
      this.setStatus("The grid did not answer.", true);
      return;
    }
    try {
      this.pool = await fetchLastNames(this.gridUrl);
    } catch {
      this.setStatus("The grid did not answer.", true);
      return;
    }
    this.setRegistering(true);
    if (this.pool) {
      this.signUp.show(this.pool, this.info?.invite_only === true);
      // A new account's password is chosen for it, not the one the
      // browser filled in for signing in.
      this.passInput.value = "";
      this.signUp.focus();
    }
  }

  private setRegistering(on: boolean): void {
    this.registering = on;
    this.setStatus("");
    const named = on && this.pool !== null;
    this.userField.style.display = named ? "none" : "";
    this.userLabel.textContent = on ? "Username" : "Name";
    this.userInput.placeholder = on ? "Username" : "First Last, username or email";
    this.signUp.root.style.display = named ? "" : "none";
    // So a password manager offers to make a new password and keep it,
    // rather than filling in an old one.
    this.passInput.autocomplete = on ? "new-password" : "current-password";
    this.passInput.placeholder = on ? `At least ${SHORTEST_PASSWORD} characters` : "Password";
    this.root.classList.toggle("signing-up", on);
    this.displayField.style.display = on && !named ? "" : "none";
    // Where to come in is for logging in, not for making an account.
    this.startField.style.display = on ? "none" : "";
    this.termsField.style.display = on && this.info?.terms_version ? "" : "none";
    this.submit.textContent = on ? "Create account" : "Log in";
    this.toggle.textContent = on ? "I already have an account" : "Create an account";
    this.cardTitle.textContent = on ? "Create your account" : "Sign in";
    this.showKept();
  }

  private setStatus(text: string, error = false): void {
    this.status.textContent = text;
    this.status.classList.toggle("error", error);
  }

  private async probe(): Promise<void> {
    const url = this.gridUrl;
    if (!url) return;
    this.gridLine.textContent = "Contacting grid…";
    try {
      const info = await fetchGridInfo(url);
      this.info = info;
      this.gridLine.replaceChildren(
        el("span", { class: "dot ok" }),
        el("b", {}, info.grid_name),
      );
      // Web links in it open in a page of their own.
      this.motd.replaceChildren(chatText(info.motd ?? "", null));
      this.motd.title = info.motd ?? "";
      this.toggle.style.display = info.registration_open ? "" : "none";
      this.showKept();
      void this.brandWords(url);
    } catch (e) {
      this.info = null;
      console.info(`grid ${url} did not answer:`, e);
      this.gridLine.replaceChildren(el("span", { class: "dot bad" }), el("b", {}, "Can't reach the grid"), el("span", { class: "muted" }, " · trying again shortly"));
      // Asked again by itself, so a grid that was restarting is found.
      setTimeout(() => {
        if (this.gridUrl === url && !this.info && this.root.isConnected) void this.probe();
      }, 15_000);
    }
    // The classic layout's boxes say the same things and were drawn once,
    // before the grid had answered, so they said "Contacting" for ever.
    this.probed = true;
    this.renderClassicBoxes();
  }

  /** The grid's own line under its name, if it has one. */
  private async brandWords(url: string): Promise<void> {
    try {
      const res = await fetch(`${url.replace(/\/$/, "")}/api/brand`);
      if (!res.ok) return;
      const brand = (await res.json()) as { tagline?: string; website?: string };
      if (this.gridUrl !== url) return;
      if (brand.tagline?.trim()) this.tagline.textContent = brand.tagline.trim();
      this.website = brand.website?.trim().replace(/\/$/, "") || null;
    } catch {
      // The line already there will do.
    }
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (this.busy) return;
    if (this.drawing === "none") {
      this.setStatus(cannotDrawMessage(), true);
      return;
    }
    if (!this.info) {
      await this.probe();
      if (!this.info) {
        this.setStatus("The grid did not answer.", true);
        return;
      }
    }
    const named = this.registering && this.pool !== null;
    const kept = this.kept();
    const username = kept ? kept.name : tidyLogin(this.userInput.value);
    const password = kept ? "" : this.passInput.value;
    const problem = named
      ? this.signUp.problem() ?? (password.length < SHORTEST_PASSWORD ? `Choose a password of at least ${SHORTEST_PASSWORD} characters.` : null)
      : !username || (!password && !kept)
        ? this.registering ? "Choose a username and a password." : "Your name and password are required."
        : null;
    if (problem) {
      this.setStatus(problem, true);
      return;
    }
    this.busy = true;
    this.submit.disabled = true;
    try {
      const terms = this.info.terms_version ?? "";
      if (this.registering) {
        if (terms && !this.termsCheck.checked) {
          this.setStatus("Please read and agree to the Terms of Service to make an account.", true);
          return;
        }
        this.setStatus("Creating your account…");
        const account: NewAccount = named
          ? { ...this.signUp.account(), password, acceptTerms: terms }
          : { username, password, displayName: this.displayInput.value.trim(), email: "", inviteCode: "", acceptTerms: terms };
        const created = await createAccount(this.info, account);
        if (!created.ok) {
          // Taken by somebody else since it was checked: asked about afresh.
          if (named) this.signUp.forget();
          this.setStatus(created.message, true);
          return;
        }
        if (named) {
          // As on the website, the address is confirmed by a link sent to
          // it before the account can come in: back to signing in, the
          // name and password ready for when it is.
          const name = this.signUp.accountName();
          this.setRegistering(false);
          this.userInput.value = name;
          saveSetting("username", name);
          this.setStatus(`Welcome, ${name}. We have sent a link to ${account.email}: open it to confirm your address, then log in here.`);
          return;
        }
      }
      this.setStatus("Logging in…");
      const startAt = this.startLocation();
      // Kept logged in, the token goes instead of the password; otherwise
      // the box says whether to be.
      const offered = this.info.keeps_logins === true;
      if (!kept && offered) saveSetting("keepLoggedIn", this.keepCheck.checked);
      let keeping: Keeping = kept ? { token: kept.token } : { remember: offered && this.keepCheck.checked };
      let response = await login(this.info, username, password, startAt, "", keeping);
      this.keepToken(username, keeping, response);
      // The grid wants the terms agreed to first: shown, and the login sent
      // again with the version agreed to (and the token it has just given,
      // the one sent being spent).
      if (!response.ok && response.terms_version) {
        this.setStatus("");
        if (!(await askToAgree(response.terms_version))) {
          this.setStatus("You need to agree to the Terms of Service to come in.", true);
          return;
        }
        this.setStatus("Logging in…");
        if (keeping.token) keeping = { token: response.login_token };
        response = await login(this.info, username, password, startAt, response.terms_version, keeping);
        this.keepToken(username, keeping, response);
      }
      if (!response.ok) {
        // No longer kept logged in: the password is asked for again, the
        // name it was kept under ready.
        if (kept && !this.kept()) this.userInput.value = kept.name;
        this.showKept();
        this.setStatus(response.message || "Login failed.", true);
        return;
      }
      saveSetting("username", username);
      // A region asked for by name that is not there: said, and where instead.
      const asked = this.startAt === "region" ? this.regionInput.value.trim() : "";
      const elsewhere = asked && asked.split("/")[0].trim().toLowerCase() !== response.region_name.toLowerCase();
      this.setStatus(elsewhere
        ? `There's no region called ${asked.split("/")[0].trim()} to come in to. Welcome, ${response.display_name}: entering ${response.region_name}…`
        : `Welcome, ${response.display_name}. Entering ${response.region_name}…`);
      this.onLogin({ info: this.info, response, gridUrl: this.gridUrl });
    } catch (err) {
      this.setStatus(plainReason((err as Error).message, "Logging in didn't work. Check your connection and try again."), true);
    } finally {
      this.busy = false;
      this.submit.disabled = false;
    }
  }

  showError(text: string): void {
    this.setStatus(text, true);
  }
}
